"use client";

import { authFetch } from "@/lib/authFetch";
import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/app/auth/AuthSessionProvider";
import { useTranslations } from "next-intl";

interface FriendTicketCountProps {
  eventId: string;
}

interface FriendTicketCountData {
  friend_count: number;
  friends_with_tickets: number;
  event_id: string;
}

const PHRASE_COUNT = 20;
const shouldLogDebug = process.env.NODE_ENV !== "production";

export const PHRASE_KEYS = [
  "phrase0",
  "phrase1",
  "phrase2",
  "phrase3",
  "phrase4",
  "phrase5",
  "phrase6",
  "phrase7",
  "phrase8",
  "phrase9",
  "phrase10",
  "phrase11",
  "phrase12",
  "phrase13",
  "phrase14",
  "phrase15",
  "phrase16",
  "phrase17",
  "phrase18",
  "phrase19",
] as const;

/**
 * phraseIndexFromEvent picks a stable variant (0..PHRASE_COUNT-1) from event + friend count
 * so copy does not flicker on re-renders and stays consistent in React Strict Mode.
 */
export function phraseIndexFromEvent(eventId: string, friendsWithTickets: number): number {
  const s = `${eventId}:${friendsWithTickets}`;
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h) % PHRASE_COUNT;
}

/**
 * Splits a leading emoji (plus optional variation selector / ZWJ sequence)
 * from a phrase so the UI can render it inside the glowing circle and keep
 * the accessible text free of emoji. Phrases without a leading emoji are
 * returned unchanged with `emoji: null`.
 */
export function splitLeadingEmoji(phrase: string): { emoji: string | null; text: string } {
  const match = /^(\p{Extended_Pictographic}(?:\uFE0F|\u200D\p{Extended_Pictographic})*)\s+(.+)$/u.exec(phrase);
  if (!match) return { emoji: null, text: phrase };
  return { emoji: match[1], text: match[2] };
}

function asFriendsWithTickets(value: unknown): number {
  const parsed = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) return 0;
  return Math.trunc(parsed);
}

export function FriendTicketCount({ eventId }: FriendTicketCountProps) {
  const t = useTranslations("events.detail.friendTicketCount");
  const { isAuthenticated } = useAuth();
  const [data, setData] = useState<FriendTicketCountData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !eventId) {
      setData(null);
      setError(null);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    setData(null);
    setError(null);

    const loadFriendTicketCount = async () => {
      const url = `/api/users/me/friends/events/${eventId}/ticket-count`;
      try {
        const response = await authFetch(url, { credentials: "include", signal: controller.signal });
        if (cancelled) return;
        if (!response.ok) {
          if (shouldLogDebug) {
            console.warn("[FriendTicketCount] Response not ok", {
              status: response.status,
              eventId,
            });
          }
          setError(`status:${response.status}`);
          setData(null);
          return;
        }
        const responseData = (await response.json()) as FriendTicketCountData;
        if (cancelled) return;
        if (typeof responseData.event_id === "string" && responseData.event_id !== eventId) {
          return;
        }
        setData(responseData);
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) {
          return;
        }
        setError(err instanceof Error ? err.message : "Failed to load data");
        setData(null);
      }
    };

    void loadFriendTicketCount();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [isAuthenticated, eventId]);

  const friendsWithTickets = asFriendsWithTickets(data?.friends_with_tickets);
  const phraseIndex = useMemo(() => {
    if (friendsWithTickets < 1) return 0;
    return phraseIndexFromEvent(eventId, friendsWithTickets);
  }, [eventId, friendsWithTickets]);

  if (!isAuthenticated || error || !data || friendsWithTickets < 1) {
    return null;
  }

  const { emoji, text } = splitLeadingEmoji(t(PHRASE_KEYS[phraseIndex], { count: friendsWithTickets }));

  return (
    <div
      className="gruv-friend-pill gruv-anim-pop"
      data-testid="friend-ticket-count-banner"
      role="status"
      aria-label={text}
    >
      {emoji ? (
        <span className="gruv-friend-pill__emoji-wrap" aria-hidden="true">
          <span className="gruv-friend-pill__glow" />
          <span className="gruv-friend-pill__emoji">{emoji}</span>
        </span>
      ) : null}
      <span className="gruv-friend-pill__text">{text}</span>
    </div>
  );
}
